import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { getCountriesForLocale, SUPPORTED_COUNTRY_CODES } from "@/lib/signupI18n";
import { normalizeLang } from "@/lib/i18n";
import { ContactAccountFields, getContactCopy } from "@/components/ContactAccountFields";
import { getInterviewReturnTarget } from "@/lib/activeInterviewNavigation";
import { partnerCodeStrings, normalizePartnerCode } from "@/lib/partnerCodeI18n";

export const COPY = {
  en: {
    eyebrow: "Account",
    title: "Account settings",
    subtitle: "Update your contact information and application language. Your email address cannot be changed.",
    email: "Email address",
    emailHelp: "Your email is your account identifier and cannot be edited.",
    firstName: "First name",
    country: "Country of residence",
    language: "Application language",
    save: "Save changes",
    saving: "Saving…",
    saved: "Account settings updated",
    cancel: "Back to app",
  },
  "pt-BR": {
    eyebrow: "Conta",
    title: "Configurações da conta",
    subtitle: "Atualize suas informações de contato e o idioma do aplicativo. Seu endereço de e-mail não pode ser alterado.",
    email: "Endereço de e-mail",
    emailHelp: "Seu e-mail é o identificador da sua conta e não pode ser editado.",
    firstName: "Primeiro nome",
    country: "País de residência",
    language: "Idioma do aplicativo",
    save: "Salvar alterações",
    saving: "Salvando…",
    saved: "Configurações da conta atualizadas",
    cancel: "Voltar ao aplicativo",
  },
  "es-419": {
    eyebrow: "Cuenta",
    title: "Configuración de la cuenta",
    subtitle: "Actualice su información de contacto y el idioma de la aplicación. Su correo electrónico no se puede cambiar.",
    email: "Correo electrónico",
    emailHelp: "Su correo es el identificador de su cuenta y no se puede editar.",
    firstName: "Nombre",
    country: "País de residencia",
    language: "Idioma de la aplicación",
    save: "Guardar cambios",
    saving: "Guardando…",
    saved: "Configuración de la cuenta actualizada",
    cancel: "Volver a la aplicación",
  },
  "fr-CA": {
    eyebrow: "Compte",
    title: "Paramètres du compte",
    subtitle: "Modifiez vos coordonnées et la langue de l’application. Votre adresse courriel ne peut pas être modifiée.",
    email: "Adresse courriel",
    emailHelp: "Votre courriel est l’identifiant de votre compte et ne peut pas être modifié.",
    firstName: "Prénom",
    country: "Pays de résidence",
    language: "Langue de l’application",
    save: "Enregistrer les modifications",
    saving: "Enregistrement…",
    saved: "Paramètres du compte mis à jour",
    cancel: "Retour à l’application",
  },
};

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "pt-BR", label: "Português (Brasil)" },
  { value: "es-419", label: "Español (Latinoamérica)" },
  { value: "fr-CA", label: "Français (Canada)" },
];

export default function AccountSettings() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const currentLang = normalizeLang(user?.preferred_language || "en");
  const t = COPY[currentLang] || COPY.en;
  const contactT = getContactCopy(currentLang);

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [telephone, setTelephone] = useState("");
  const [country, setCountry] = useState((user?.country || "").toUpperCase());
  const [language, setLanguage] = useState(currentLang);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/account/contact")
      .then(({ data }) => {
        if (!active) return;
        const c = data?.contact || {};
        setFirstName(c.first_name || user?.first_name || "");
        setLastName(c.last_name || "");
        setStreetAddress(c.street_address || "");
        setCity(c.city || "");
        setRegion(c.region || "");
        setPostalCode(c.postal_code || "");
        setTelephone(c.telephone || "");
        setCountry((c.country || user?.country || "").toUpperCase());
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user]);

  const countryOptions = useMemo(() => getCountriesForLocale(language), [language]);
  const valid = [firstName, lastName, streetAddress, city, region, postalCode, telephone].every((v) => v.trim().length > 0)
    && SUPPORTED_COUNTRY_CODES.includes(country)
    && !busy
    && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await api.patch("/account/settings", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        street_address: streetAddress.trim(),
        city: city.trim(),
        region: region.trim(),
        postal_code: postalCode.trim(),
        country,
        telephone: telephone.trim(),
        preferred_language: language,
      });
      await refresh();
      toast.success((COPY[language] || COPY.en).saved);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex, language));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] px-4 py-10" data-testid="account-settings">
      <div className="mx-auto max-w-xl rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 sm:p-8 shadow-sm">
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)]">{t.eyebrow}</div>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-secondary)]">{t.subtitle}</p>
        <p className="mt-2 text-xs leading-relaxed text-[color:var(--text-secondary)]">{contactT.contactPurpose}</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <label className="block">
            <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{t.email}</span>
            <input type="email" value={user?.email || ""} readOnly className="w-full rounded-lg border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] px-3 py-2 text-sm text-[color:var(--text-secondary)]" data-testid="account-email" />
            <span className="mt-1 block text-[11px] text-[color:var(--text-secondary)]">{t.emailHelp}</span>
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{t.firstName}</span>
            <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm" data-testid="account-first-name" />
          </label>

          <ContactAccountFields
            locale={language}
            lastName={lastName} setLastName={setLastName}
            streetAddress={streetAddress} setStreetAddress={setStreetAddress}
            city={city} setCity={setCity}
            region={region} setRegion={setRegion}
            postalCode={postalCode} setPostalCode={setPostalCode}
            telephone={telephone} setTelephone={setTelephone}
          />

          <label className="block">
            <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{t.country}</span>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full rounded-lg border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm" data-testid="account-country">
              <option value="" disabled>—</option>
              {countryOptions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{t.language}</span>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="w-full rounded-lg border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm" data-testid="account-language">
              {LANGUAGES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>

          <div className="flex flex-wrap gap-3 pt-3">
            <button type="submit" disabled={!valid} className="rounded-full bg-[color:var(--brand)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[color:var(--brand-hover)] disabled:opacity-40" data-testid="account-save">{busy ? t.saving : t.save}</button>
            <button type="button" onClick={() => navigate(getInterviewReturnTarget("/"))} className="rounded-full border border-[color:var(--border-stone)] px-5 py-2.5 text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]" data-testid="account-back">{t.cancel}</button>
          </div>
        </form>

        <PartnerCodeSection language={language} onApplied={refresh} />
      </div>
    </div>
  );
}

// Partner Code (ACCOUNT_TIER_RULES.md, Beta Tester standards, 2026-10-30): a tester enters a
// new code here to renew; a code entered before the end adds 30 days to it.
function PartnerCodeSection({ language, onApplied }) {
  const pc = partnerCodeStrings(language);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/account/partner-code", { code });
      const until = data?.access_expires_at ? new Date(data.access_expires_at).toLocaleDateString() : "";
      toast.success(pc.applied(until));
      setCode("");
      await onApplied();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex, language));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mt-10 border-t border-[color:var(--border-stone)] pt-6" data-testid="account-partner-code">
      <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">{pc.settingsTitle}</h2>
      <p className="mt-1 text-xs text-[color:var(--text-secondary)]">{pc.settingsHint}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input type="text" value={code} onChange={(e) => setCode(normalizePartnerCode(e.target.value))} placeholder={pc.placeholder} maxLength={32} autoComplete="off" spellCheck={false} className="flex-1 min-w-[12rem] rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm font-mono tracking-wider" data-testid="account-partner-code-input" />
        <button type="button" onClick={apply} disabled={!code || busy} className="rounded-full bg-[color:var(--brand)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[color:var(--brand-hover)] disabled:opacity-40" data-testid="account-partner-code-apply">{busy ? pc.applying : pc.apply}</button>
      </div>
    </section>
  );
}
